/* eslint-disable no-spaced-func */
/** @jsx jsx */

import { Fragment, useRef, useState } from 'react';
import {
  Button,
  Colors,
  FloatingBox,
  IconName,
  Menu,
  MenuItem,
  Modal,
  ModalContent,
  ModalContentBody,
  ModalContentFooter,
  ModalContentHeader,
  Spacer,
} from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import MenuMoreToggler from 'app/common/components/MenuMoreToggler';
import OutsideClickable from 'app/developer-v3/components/OutsideClickable';

const styleRoot = css`
  position: relative;
  white-space: nowrap;
  text-align: right;
`;

const styleMenuContainerOverrides = css`
  [class*='FloatingBox__box'] {
    padding: 10px !important;

    [class*='MenuItem__text'] {
      text-align: left !important;
    }

    ul li:last-child button {
      &[aria-label*='Delete'][class*='MenuItem__content--selected'][class][class][class][class][class] {
        background-color: ${Colors.Red4};
        color: ${Colors.PrimeWhite};
      }

      &[aria-label*='Delete'][class][class][class][class][class]:hover {
        background-color: ${Colors.Red4};
        color: ${Colors.PrimeWhite};
      }
    }
  }
`;

const styleModalWrapper = css`
  text-align: initial;
`;

type Item = {
  /** Will trigger a confirmation when the option is chosen */
  isConfirmationRequired?: boolean;
  /** Renders a disabled option */
  isDisabled?: boolean;
  /** Text to be rendered for the user */
  label: string;
  /** Optional text to be displayed in the confirmation modal body */
  modalTextContent?: string;
  /** Optional text to be displayed in the confirmation modal title */
  modalTitle?: string;
  /** Callback that handles the clicked option */
  onChoose?: () => void;
  /** Key value of each option*/
  value: string;
};

type Props = {
  /** Modal confirmation button text */
  confirmationText?: string;
  /** Icon that triggers the dropdown. The default is `navCogFill` */
  iconName?: IconName;
  /** Array of actions to be rendered */
  items: Item[];
};

/** Renders a menu toggle with a gear icon. Clicking opens a menu of items. */
export default function ActionsDropdownMenu({
  confirmationText = 'Confirm',
  items,
}: Props) {
  const [itemPendingConfirmation, setItemPendingConfirmation] = useState<
    Item
  >();

  const [isOpen, setIsOpen] = useState(false);
  const iconButtonContainerRef = useRef<HTMLDivElement>();

  function onClickOutside() {
    if (!isOpen || itemPendingConfirmation) {
      return;
    }
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setIsOpen(false);
  }

  function closeMenu() {
    setIsOpen(false);
    setItemPendingConfirmation(undefined);
  }

  function focusButton() {
    if (iconButtonContainerRef.current) {
      const button = iconButtonContainerRef.current.querySelector('button');
      if (button) {
        button.focus();
      }
    }
  }

  function onClick(item: Item) {
    if (item.isDisabled) {
      return;
    }
    if (item.isConfirmationRequired) {
      setItemPendingConfirmation(item);
      return;
    }
    if (item.onChoose) {
      item.onChoose();
    }
    closeMenu();
    focusButton();
  }

  function modalCancel() {
    setItemPendingConfirmation(undefined);
    closeMenu();
    focusButton();
  }

  function modalConfirm() {
    if (itemPendingConfirmation?.onChoose) {
      itemPendingConfirmation.onChoose();
    }
    setItemPendingConfirmation(undefined);
    closeMenu();
    focusButton();
  }

  return (
    <Fragment>
      {itemPendingConfirmation && (
        <div css={styleModalWrapper}>
          <Modal onClosed={modalCancel}>
            <ModalContent>
              <ModalContentHeader>
                {itemPendingConfirmation.modalTitle}
              </ModalContentHeader>
              <ModalContentBody>
                {itemPendingConfirmation.modalTextContent}
              </ModalContentBody>
              <Spacer height="10px" />
              <ModalContentFooter>
                <Button color="secondary" onClick={modalCancel}>
                  Cancel
                </Button>
                <Button color="primary" onClick={modalConfirm}>
                  {confirmationText}
                </Button>
              </ModalContentFooter>
            </ModalContent>
          </Modal>
        </div>
      )}
      <OutsideClickable onClickOutside={onClickOutside}>
        <div css={styleRoot}>
          <div ref={iconButtonContainerRef}>
            <MenuMoreToggler
              aria-label="Options toggler"
              onClick={() => {
                if (isOpen) {
                  closeMenu();
                } else {
                  setIsOpen(true);
                }
              }}
            />
          </div>
          {isOpen && (
            <div css={styleMenuContainerOverrides}>
              <FloatingBox align="right" borderRadius="small" minWidth="150px">
                <Menu aria-label="Options menu" role="menu">
                  {items.map(item => (
                    <MenuItem
                      aria-label={item.label}
                      disabled={item.isDisabled}
                      key={item.value}
                      onClick={() => {
                        onClick(item);
                      }}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </Menu>
              </FloatingBox>
            </div>
          )}
        </div>
      </OutsideClickable>
    </Fragment>
  );
}
